import { ReactElement, useCallback, useState } from "react";

// Third parties
import { useDispatch, useSelector } from "react-redux";

// Evergiving
import {
  CustomInputTag,
  CustomCheckboxFieldConfig,
} from "@evergiving/common/dist/es2015/form";

// Form components
import { LabelledField } from "@/lib/components/LabelledField";
import { LabelText } from "@/lib/components/LabelText";
import { FieldError } from "@/lib/components/FieldError";
import { UICheckbox } from "@/lib/components/UICheckbox";

// Hooks
import { useShowErrors } from "@/lib/hooks/useShowErrors";
import { useFieldValidationErrors } from "@/lib/hooks/useFieldValidationErrors";

// Redux
import { setFormValue, setCustomInputFormValue } from "@/lib/redux/actions";
import { RootState, VersionedStringFormState } from "@/lib/redux/types";

// Validations
import { normalizeFieldValue } from "@/lib/misc/validations";

export function CustomCheckboxField({
  config,
  name,
}: {
  config: CustomCheckboxFieldConfig;
  name: keyof VersionedStringFormState;
}): ReactElement {
  const value = useSelector((state: RootState) => state[name].value);
  const dispatch = useDispatch();
  const anyErrors = useShowErrors(config, name, value || config.uncheckedValue);
  const fieldValidationErrors = useFieldValidationErrors(config);
  const [isDirty, setIsDirty] = useState(false);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = normalizeFieldValue(
        event.target.checked ? config.checkedValue : config.uncheckedValue
      );
      dispatch(
        setFormValue(
          name,
          newValue,
          fieldValidationErrors.forValue(newValue).length > 0
        )
      );

      if (name.includes("customInput_") && newValue) {
        const customInputTag: CustomInputTag = name.split("customInput_")[1];
        dispatch(setCustomInputFormValue(customInputTag, newValue));
      }

      setIsDirty(true);
    },
    [dispatch, config, fieldValidationErrors, name]
  );

  const { requiredFieldErrorText, labelText } = config;
  const required = !!requiredFieldErrorText;

  const errorId = `${name}-errors`;

  return (
    <div>
      <LabelledField id={name}>
        <div className="checkboxField">
          <UICheckbox
            isChecked={value === config.checkedValue}
            onChange={handleChange}
          />
          <LabelText requiredIndicator={required}>{labelText}</LabelText>
        </div>
        <div role="alert">
          {anyErrors && isDirty ? (
            <FieldError
              id={errorId}
              messages={fieldValidationErrors.forValue(value)}
            />
          ) : null}
        </div>
      </LabelledField>
    </div>
  );
}
